/* API client for the SnappyMail JSON API — replicates the verified request
 * flow exactly (see ui/scripts/probe-*.mjs for the live capture):
 *
 *   1. GET  ?/AppData/0/<rand>/          → System.token ("0-…")
 *   2. POST ?/Json/&q[]=/0/  Login       → Result.System.token ("1-…") — ROTATED, use for everything after
 *   3. POST ?/Json/&q[]=/0/  Folders, MessageList, …
 *   4. GET  ?/Json/&q[]=/0/Message/&q[]=/<b64>  → full message
 *
 * All requests are same-origin (dev: Vite proxies `/?/…` to the PHP backend),
 * so the smtoken/smsession/smaccount cookies flow automatically.
 */
import type {
    AccountsAndIdentitiesResult,
    Attachment,
    FolderCollection,
    LoginData,
    Message,
    MessageCollection,
    MessageDraftParams,
    MessageListItem,
    StagedAttachment,
} from "./types";

const JSON_BASE = "/?/Json/&q[]=/0/";

let csrfToken = "";

export class ApiError extends Error {
    code: number;
    constructor(code: number, message: string) {
        super(message);
        this.code = code;
    }
}

interface JsonEnvelope<T> {
    Action: string;
    Result: T | false;
    code?: number;
    message?: string;
    messageAdditional?: string;
}

function urlSafeB64(input: string): string {
    return btoa(input).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function post<T>(action: string, params: object = {}): Promise<T> {
    const res = await fetch(JSON_BASE, {
        method: "POST",
        credentials: "same-origin",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-SM-Token": csrfToken,
        },
        body: JSON.stringify({ Action: action, ...params }),
    });
    if (!res.ok) throw new ApiError(res.status, `HTTP ${res.status}`);
    const data = (await res.json()) as JsonEnvelope<T>;
    if (data.Result === false || data.code) {
        throw new ApiError(data.code ?? 0, data.message ?? data.messageAdditional ?? `Action ${action} failed`);
    }
    return data.Result as T;
}

/* The API returns `null` for collection fields when empty (e.g. a message
 * without attachments has `attachments: null`). Normalize to empty objects so
 * components can rely on the documented shapes. */
export function normalizeMessage<T extends MessageListItem>(msg: T): T {
    return {
        ...msg,
        attachments:
            msg.attachments ?? { "@Object": "Collection/AttachmentCollection", "@Collection": [] },
        flags: msg.flags ?? [],
        from: msg.from ?? [],
        to: msg.to ?? [],
        cc: msg.cc ?? [],
        bcc: msg.bcc ?? [],
        replyTo: msg.replyTo ?? [],
        sender: msg.sender ?? [],
        deliveredTo: msg.deliveredTo ?? [],
        headers:
            msg.headers ?? { "@Object": "Collection/MimeHeaderCollection", "@Collection": [] },
    };
}

async function getJson(url: string): Promise<unknown> {
    const res = await fetch(url, {
        credentials: "same-origin",
        headers: { Accept: "application/json", "X-SM-Token": csrfToken },
    });
    if (!res.ok) throw new ApiError(res.status, `HTTP ${res.status}`);
    return res.json();
}

export interface AppData {
    System: { token: string };
}

/** Boot: fetch AppData, store the initial CSRF token. */
export async function appData(): Promise<AppData> {
    const data = (await getJson(`/?/AppData/0/${Math.random().toString().slice(2)}/`)) as AppData;
    csrfToken = data.System.token;
    return data;
}

export interface LoginParams {
    email: string;
    password: string;
    signMe?: boolean;
}

/** Login — on success the response rotates the CSRF token; store it. */
export async function login({ email, password, signMe = false }: LoginParams): Promise<LoginData> {
    const data = (await post<LoginData>("Login", {
        Email: email,
        Password: password,
        language: "",
        signMe: signMe ? 1 : 0,
    })) as LoginData;
    csrfToken = data.System.token;
    return data;
}

export async function folders(): Promise<FolderCollection> {
    return post<FolderCollection>("Folders");
}

export interface MessageListParams {
    folder: string;
    offset?: number;
    limit?: number;
    uidNext?: number;
    sort?: string;
    search?: string;
    threadUid?: number;
}

export async function messageList(params: MessageListParams): Promise<MessageCollection> {
    const result = await post<MessageCollection>("MessageList", {
        folder: params.folder,
        offset: params.offset ?? 0,
        limit: params.limit ?? 20,
        uidNext: params.uidNext ?? 0,
        sort: params.sort ?? "",
        search: params.search ?? "",
        threadUid: params.threadUid ?? 0,
    });
    result["@Collection"] = result["@Collection"].map(normalizeMessage);
    return result;
}

/** Fetch the full message body for a list item. */
export async function message(folder: string, uid: number, accountHash: string): Promise<Message> {
    const rawKey = urlSafeB64(JSON.stringify([folder, uid, 0, accountHash]));
    const data = (await getJson(`/?/Json/&q[]=/0/Message/&q[]=/${rawKey}`)) as JsonEnvelope<Message>;
    if (data.Result === false || data.code) {
        throw new ApiError(data.code ?? 0, data.message ?? "Failed to load message");
    }
    return normalizeMessage(data.Result as Message);
}

/** Bare raw key (url-safe b64 of the attachment locator). This is what
 * MessageUploadAttachments consumes — NOT the full URL. */
export function downloadKey(attachment: Attachment, accountHash: string): string {
    return urlSafeB64(
        JSON.stringify({
            folder: attachment.folder,
            uid: attachment.uid,
            mimeIndex: attachment.mimeIndex,
            mimeType: attachment.mimeType,
            fileName: attachment.fileName,
            accountHash,
        })
    );
}

/** Raw download URL for an attachment (?/Raw/…Download…). Plain <a> works — cookies do the auth. */
export function downloadUrl(attachment: Attachment, accountHash: string): string {
    return `/?/Raw/&q[]=/0/Download/&q[]=/${downloadKey(attachment, accountHash)}`;
}

/** Proxy external images through the webmail server (privacy: no direct leaks). */
export function proxyUrl(url: string): string {
    const encoded = btoa(url.replace(/ /g, "%20")).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
    return `/?/ProxyExternal/${encoded}`;
}

/** Mark messages as seen/unseen (optimistic in the UI). */
export async function setSeen(folder: string, uids: number[], seen: boolean): Promise<void> {
    await post<unknown>("MessageSetSeen", {
        folder,
        uids: uids.join(","),
        setAction: seen ? 1 : 0,
    });
}

/** Flag/unflag messages (★). */
export async function setFlagged(folder: string, uids: number[], flagged: boolean): Promise<void> {
    await post<unknown>("MessageSetFlagged", {
        folder,
        uids: uids.join(","),
        setAction: flagged ? 1 : 0,
    });
}

/** Move messages to another folder. Legacy marks them read when moving to
 * Trash/Spam (markAsRead=1), matching upstream Messagelist#moveMessages. */
export async function messageMove(
    fromFolder: string,
    toFolder: string,
    uids: number[],
    markAsRead = false
): Promise<void> {
    await post<unknown>("MessageMove", {
        fromFolder,
        toFolder,
        uids: uids.join(","),
        markAsRead: markAsRead ? 1 : 0,
    });
}

/** Hard-delete messages (used when already in Trash — upstream falls back to
 * MessageDelete only there; everywhere else Delete is a move to Trash). */
export async function messageDelete(folder: string, uids: number[]): Promise<void> {
    await post<unknown>("MessageDelete", { folder, uids: uids.join(",") });
}

/** "Download" existing message attachments into the upload store (used for
 * reply/forward attach-from-message). Returns [{tempName, mimeType}] aligned
 * with the input download hashes. */
export async function messageUploadAttachments(downloads: string[]): Promise<{ tempName: string; mimeType: string }[]> {
    return post<{ tempName: string; mimeType: string }[]>("MessageUploadAttachments", { attachments: downloads });
}

/** Identities for the compose From selector. */
export async function accountsAndIdentities(): Promise<AccountsAndIdentitiesResult> {
    return post<AccountsAndIdentitiesResult>("AccountsAndIdentities");
}

/** Recipient autocomplete (contacts + history). Result: [[email, name], …]. */
export async function suggestions(query: string): Promise<[string, string][]> {
    return post<[string, string][]>("Suggestions", { Query: query });
}

/** Upload a file into the server-side attachment store (?/Upload, field `uploader`). */
export async function uploadAttachment(file: File): Promise<StagedAttachment> {
    const fd = new FormData();
    fd.append("uploader", file, file.name);
    const res = await fetch(`/?/Upload/&q[]=/0/`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "X-SM-Token": csrfToken },
        body: fd,
    });
    if (!res.ok) throw new ApiError(res.status, `Upload failed (HTTP ${res.status})`);
    const data = (await res.json()) as JsonEnvelope<{ Attachment?: StagedAttachment }>;
    const att = data.Result === false ? undefined : (data.Result as { Attachment?: StagedAttachment }).Attachment;
    if (data.code || !att?.tempName) {
        throw new ApiError(data.code ?? 0, data.message ?? "Upload failed");
    }
    return att as StagedAttachment;
}

/** Build the attachments map for SendMessage/SaveMessage from staged uploads. */
export function attachmentsParam(staged: StagedAttachment[]): MessageDraftParams["attachments"] {
    const out: MessageDraftParams["attachments"] = {};
    for (const a of staged) {
        out[a.tempName] = { name: a.name, inline: false, cId: "", location: "", type: a.mimeType };
    }
    return out;
}

/** Send a message. Result is `true` on success. */
export async function sendMessage(params: MessageDraftParams): Promise<boolean> {
    return post<boolean>("SendMessage", params);
}

/** Save a draft. Returns {folder, uid}; pass messageFolder/messageUid to update (old copy deleted). */
export async function saveMessage(params: MessageDraftParams): Promise<{ folder: string; uid: number }> {
    return post<{ folder: string; uid: number }>("SaveMessage", params);
}

/** Persist user settings. Only keys present in `patch` are updated server-side
 * (see RainLoop\Actions\User::DoSettingsUpdate). Pass the exact key names from
 * the AppData/login response (e.g. `MessagesPerPage`, `AutoLogout`). */
export async function settingsUpdate(patch: Record<string, string | number | boolean>): Promise<boolean> {
    return post<boolean>("SettingsUpdate", patch);
}

/** Persist an identity (create or update — keyed by `id`). */
export async function identityUpdate(identity: {
    Id?: string;
    Label?: string;
    Email: string;
    Name?: string;
    ReplyTo?: string;
    Bcc?: string;
    Signature?: string;
    SignatureInsertBefore?: boolean;
    sentFolder?: string;
}): Promise<boolean> {
    return post<boolean>("IdentityUpdate", identity);
}

/** Delete an identity by id. */
export async function identityDelete(id: string): Promise<boolean> {
    return post<boolean>("IdentityDelete", { idToDelete: id });
}

/** Fetch a threaded view of one message (servers with THREAD capability). */
export async function threadList(
    folder: string,
    threadUid: number
): Promise<MessageCollection> {
    return post<MessageCollection>("MessageList", {
        folder,
        offset: 0,
        limit: 50,
        uidNext: 0,
        sort: "",
        search: "",
        useThreads: 1,
        threadAlgorithm: "",
        threadUid,
    });
}

export type { MessageListItem };
